import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Award, Download, FileText, History, Loader2, Receipt, Sparkles, Ticket } from "lucide-react";

const fmtEur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

const downloadBlob = async (url, filename, opts = {}) => {
  const token = localStorage.getItem("token");
  const res = await fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return res;
};

/**
 * StudentActionsPanel — superadmin-only actions for an alumno's account.
 *
 * Rendered inside the "Cursos del usuario" dialog when the viewer is a
 * superadmin. Provides:
 *   · Registrar pago manual → creates a ticket (`AÑO-TKnnnn`)
 *   · Histórico de pagos    → lists tickets + invoices with download + promotion
 *   · Emitir diploma manual → PDF diploma/certificado on demand
 *
 * The name is kept as ``AplActionsPanel`` for backward-compat with the file
 * import in ``AdminUsers.jsx``; internally the component no longer depends
 * on the APL/TK flags (removed in Feb-2026 restructure).
 */
export default function AplActionsPanel({ user, courses }) {
  return (
    <Card className="p-4 border border-purple-200 bg-purple-50/40 rounded-lg space-y-3" data-testid="student-actions-panel">
      <div className="flex items-center gap-2 text-purple-900">
        <span className="inline-flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 rounded border bg-purple-200 text-purple-900 border-purple-300">
          SA
        </span>
        <p className="text-sm font-semibold">Acciones de superadmin</p>
      </div>
      <p className="text-xs text-purple-800/80">
        Registra pagos, revisa el histórico y promueve tickets a facturas. Todos los pagos generan un ticket automáticamente.
      </p>
      <div className="flex flex-wrap gap-2">
        <ManualPaymentButton user={user} />
        <PaymentHistoryButton user={user} />
        <ManualCertificateButton user={user} courses={courses} />
      </div>
    </Card>
  );
}

/* -------------------- 1) Manual payment (creates ticket) -------------------- */
function ManualPaymentButton({ user }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ amount: "", concept: "" });
  const [issuedNumber, setIssuedNumber] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ amount: "", concept: "" });
      setIssuedNumber(null);
    }
  }, [open]);

  const submit = async () => {
    const amount = parseFloat((form.amount || "").toString().replace(",", "."));
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error("Importe inválido");
      return;
    }
    if (!form.concept.trim()) {
      toast.error("Introduce el concepto del pago");
      return;
    }
    setSaving(true);
    try {
      const res = await downloadBlob(
        `${API}/admin/users/${user.id}/manual-payment`,
        "ticket.pdf",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount,
            concept: form.concept.trim(),
          }),
        },
      );
      const tkNo = res.headers.get("X-Ticket-Number");
      setIssuedNumber(tkNo);
      toast.success(`Ticket ${tkNo || ""} generado`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-purple-700 hover:bg-purple-800 text-white"
        data-testid="sa-open-manual-payment"
      >
        <Receipt className="w-4 h-4 mr-1" />
        Registrar pago
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="sa-manual-payment-modal">
          <DialogHeader>
            <DialogTitle>Registrar pago manual</DialogTitle>
            <DialogDescription>
              Genera un ticket con numeración correlativa (AÑO-TKnnnn) usando los datos de facturación de Ajustes. Si el cliente necesita una factura formal, después podrás emitirla desde el histórico.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Importe (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))}
                  placeholder="25,00"
                  data-testid="sa-payment-amount"
                />
              </div>
              <div className="flex items-end text-xs text-gray-500">
                Base imponible. El IVA se aplica según Ajustes.
              </div>
            </div>
            <div>
              <Label className="text-xs">Concepto *</Label>
              <Textarea
                rows={2}
                value={form.concept}
                onChange={(e) => setForm((s) => ({ ...s, concept: e.target.value }))}
                placeholder="Curso presencial de primeros auxilios · Enero 2026"
                data-testid="sa-payment-concept"
              />
            </div>
            {issuedNumber && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                Ticket {issuedNumber} generado correctamente. Descarga iniciada.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cerrar</Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-purple-700 hover:bg-purple-800 text-white"
              data-testid="sa-submit-manual-payment"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generando…</> : <><Ticket className="w-4 h-4 mr-1" /> Generar ticket</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- 2) Payment history -------------------- */
function PaymentHistoryButton({ user }) {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [promoteTx, setPromoteTx] = useState(null);

  const refresh = async () => {
    setItems(null);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/invoices`);
      setItems(data);
    } catch {
      toast.error("No se pudo cargar el histórico");
    }
  };

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const download = async (invoiceId, invoiceNo, isTicket) => {
    try {
      const prefix = isTicket ? "ticket" : "factura";
      await downloadBlob(
        `${API}/admin/invoices/${invoiceId}/pdf`,
        `${prefix}-${invoiceNo || invoiceId}.pdf`,
      );
    } catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="bg-white"
        data-testid="sa-open-payment-history"
      >
        <History className="w-4 h-4 mr-1" />
        Histórico de pagos
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="sa-payment-history-modal">
          <DialogHeader>
            <DialogTitle>Histórico de pagos</DialogTitle>
            <DialogDescription>
              Todos los pagos del alumno. Como superadmin, puedes promover cualquier ticket a factura formal con datos del cliente.
            </DialogDescription>
          </DialogHeader>
          {items === null ? (
            <p className="text-sm text-gray-500 py-6 text-center">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="sa-payment-history-empty">
              Sin pagos registrados aún.
            </p>
          ) : (
            <table className="w-full text-sm mt-2" data-testid="sa-payment-history-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b">
                  <th className="text-left py-2">Nº</th>
                  <th className="text-left py-2">Tipo</th>
                  <th className="text-left py-2">Fecha</th>
                  <th className="text-left py-2">Concepto</th>
                  <th className="text-right py-2">Importe</th>
                  <th className="py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const isTicket = it.document_kind === "ticket";
                  const isGeneric = it.document_kind === "pago";
                  return (
                    <tr key={it.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`sa-payment-row-${it.id}`}>
                      <td className="py-2 font-mono-data text-brand-ink">
                        {it.invoice_number || "—"}
                        {it.original_ticket_number && it.document_kind === "invoice" && (
                          <span className="block text-[10px] text-gray-400 font-mono-data" title="Ticket original antes de promover">
                            ex-{it.original_ticket_number}
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        {isGeneric ? (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-gray-50 text-gray-700 border border-gray-200">PAGO</span>
                        ) : isTicket ? (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">TICKET</span>
                        ) : (
                          <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-brand-soft text-brand-ink border border-[#bcdfd9]">FACTURA</span>
                        )}
                      </td>
                      <td className="py-2 text-gray-600">{fmtDate(it.paid_at || it.created_at)}</td>
                      <td className="py-2 max-w-xs truncate" title={it.concept}>{it.concept}</td>
                      <td className="py-2 text-right font-mono-data">{fmtEur(it.amount_eur)}</td>
                      <td className="py-2 text-right space-x-1">
                        {!isGeneric && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => download(it.id, it.invoice_number, isTicket)}
                            data-testid={`sa-payment-download-${it.id}`}
                            title="Descargar PDF"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {isSuperadmin && isTicket && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPromoteTx(it)}
                            data-testid={`sa-promote-${it.id}`}
                            title="Generar factura de este ticket"
                            className="text-purple-700 hover:bg-purple-50"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50" data-testid="sa-payment-history-total-row">
                  <td colSpan="4" className="py-3 text-right text-[11px] uppercase tracking-wider text-gray-600 font-mono-data">
                    Total ({items.length}) ·
                  </td>
                  <td className="py-3 text-right font-display text-lg text-brand-ink" data-testid="sa-payment-history-total">
                    {fmtEur(items.reduce((acc, it) => acc + (Number(it.amount_eur) || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </DialogContent>
      </Dialog>
      <PromoteTicketModal
        tx={promoteTx}
        user={user}
        onClose={() => setPromoteTx(null)}
        onPromoted={() => { setPromoteTx(null); refresh(); }}
      />
    </>
  );
}

function PromoteTicketModal({ tx, user, onClose, onPromoted }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", tax_id: "",
    address: "", postal_code: "", city: "", country: "ES",
  });

  useEffect(() => {
    if (tx && user) {
      setForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        tax_id: user.dni || "",
        address: user.billing?.address || "",
        postal_code: user.billing?.postal_code || "",
        city: user.billing?.city || "",
        country: user.billing?.country || "ES",
      });
    }
  }, [tx, user]);

  if (!tx) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const res = await downloadBlob(
        `${API}/admin/tx/${tx.id}/generate-invoice`,
        `factura.pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );
      const invNo = res.headers.get("X-Invoice-Number");
      toast.success(`Factura ${invNo || ""} emitida`);
      onPromoted();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="sa-promote-ticket-modal">
        <DialogHeader>
          <DialogTitle>Generar factura del ticket {tx.invoice_number}</DialogTitle>
          <DialogDescription>
            Se creará una nueva factura correlativa (AÑO-Fnnnn) vinculada a este ticket. Los datos del alumno están precargados — ajústalos si el cliente factura a otra razón social.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nombre</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="sa-promote-firstname" />
            </div>
            <div>
              <Label>Apellidos / Razón social</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="sa-promote-lastname" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>CIF / NIF / NIE</Label>
              <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value.toUpperCase() })} data-testid="sa-promote-tax" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sa-promote-email" />
            </div>
          </div>
          <div>
            <Label>Dirección</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="sa-promote-address" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>C.P.</Label>
              <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Ciudad</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-purple-700 hover:bg-purple-800 text-white"
            data-testid="sa-promote-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Emitir factura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- 3) Manual exam result + course diploma -------------------- */
function ManualCertificateButton({ user, courses }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [form, setForm] = useState({
    course_id: "",
    exam_id: "",
    score: "",
  });
  const [courseStatus, setCourseStatus] = useState(null);

  useEffect(() => {
    if (open && courses && courses.length > 0 && !form.course_id) {
      const first = courses[0];
      setForm((f) => ({ ...f, course_id: first.course_id, exam_id: first.exams?.[0]?.exam_id || "" }));
    }
  }, [open, courses, form.course_id]);

  const selected = (courses || []).find((c) => c.course_id === form.course_id);

  useEffect(() => {
    // Reset exam selection (and cached course status) whenever the course changes.
    setCourseStatus(null);
    if (selected && !selected.exams?.some((e) => e.exam_id === form.exam_id)) {
      setForm((f) => ({ ...f, exam_id: selected.exams?.[0]?.exam_id || "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.course_id]);

  const selectedExam = (selected?.exams || []).find((e) => e.exam_id === form.exam_id);

  const submitScore = async () => {
    if (!form.course_id || !form.exam_id) {
      toast.error("Selecciona un curso y un examen");
      return;
    }
    const s = parseFloat((form.score || "").toString().replace(",", "."));
    if (Number.isNaN(s) || s < 0 || s > 100) {
      toast.error("Puntuación entre 0 y 100");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/manual-exam-result`, {
        course_id: form.course_id,
        exam_id: form.exam_id,
        score: s,
      });
      setCourseStatus(data.course_status);
      toast.success(data.attempt.passed ? "Examen marcado como aprobado" : "Examen marcado como suspendido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la puntuación");
    } finally {
      setSaving(false);
    }
  };

  const downloadDiploma = async () => {
    setDownloading(true);
    try {
      const filename = `${selected?.kind === "final" ? "diploma" : "certificado"}-${(selected?.title || "curso").replace(/\s+/g, "_")}.pdf`;
      await downloadBlob(
        `${API}/admin/users/${user.id}/courses/${form.course_id}/diploma`,
        filename,
      );
      toast.success("Documento descargado");
    } catch (e) {
      toast.error(e.message || "No se pudo descargar");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="bg-white"
        data-testid="sa-open-manual-certificate"
      >
        <Award className="w-4 h-4 mr-1" />
        Puntuación manual / diploma
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="sa-manual-cert-modal">
          <DialogHeader>
            <DialogTitle>Puntuación manual y diploma/certificado</DialogTitle>
            <DialogDescription>
              Marca manualmente el resultado de un examen (para formación presencial sin test
              online, o para sobrescribir una nota). El diploma/certificado del curso solo se
              podrá descargar cuando TODOS sus exámenes estén aprobados.
            </DialogDescription>
          </DialogHeader>
          {!courses || courses.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              Este alumno no tiene cursos asignados.
            </p>
          ) : (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Curso *</Label>
                <Select
                  value={form.course_id}
                  onValueChange={(v) => setForm({ ...form, course_id: v, exam_id: "" })}
                >
                  <SelectTrigger data-testid="sa-cert-course">
                    <SelectValue placeholder="Selecciona un curso" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => (
                      <SelectItem key={c.course_id} value={c.course_id}>
                        {c.kind === "final" ? "🎓" : "📜"} {c.title}
                        {c.passed ? ` · Todos aprobados (${c.final ?? "—"}%)` : c.attempts > 0 ? " · En progreso" : " · Sin intentos"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selected && (
                <div>
                  <Label>Examen *</Label>
                  <Select
                    value={form.exam_id}
                    onValueChange={(v) => setForm({ ...form, exam_id: v })}
                  >
                    <SelectTrigger data-testid="sa-cert-exam">
                      <SelectValue placeholder="Selecciona un examen" />
                    </SelectTrigger>
                    <SelectContent>
                      {(selected.exams || []).map((e) => (
                        <SelectItem key={e.exam_id} value={e.exam_id}>
                          {e.title} — {e.status === "passed" ? "Aprobado" : e.status === "locked" ? "Bloqueado" : "Pendiente"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedExam?.status === "passed" && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Este examen ya está aprobado (mejor nota: {selectedExam.best_score ?? "—"}%).
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label>Puntuación manual (0-100) *</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.score}
                  onChange={(e) => setForm({ ...form, score: e.target.value })}
                  placeholder="Ej. 8,5 o 85"
                  className="mt-1 max-w-[140px]"
                  data-testid="sa-cert-manual-score"
                />
              </div>

              {courseStatus && (
                <div
                  className={`text-sm rounded-md p-3 border ${
                    courseStatus.diploma_ready
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}
                  data-testid="sa-cert-course-status"
                >
                  {courseStatus.diploma_ready
                    ? "Todos los exámenes están aprobados y el diploma/certificado ya se puede descargar."
                    : courseStatus.all_exams_passed
                    ? "Todos los exámenes están aprobados, pero el curso tiene un plan de pago aplazado aún abierto."
                    : "Aún quedan exámenes por aprobar en este curso."}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-3 flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving || downloading}>Cerrar</Button>
            {courseStatus?.diploma_ready && (
              <Button
                onClick={downloadDiploma}
                disabled={downloading}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="sa-cert-download"
              >
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Descargar {selected?.kind === "final" ? "diploma" : "certificado"}
              </Button>
            )}
            <Button
              onClick={submitScore}
              disabled={saving || !form.course_id || !form.exam_id}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="sa-cert-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Guardar puntuación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
